import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { iceLogo } from '../mock';

const KEY = 'bjp-install-prompt-dismissed';
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIOS = () => /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || window.localStorage.getItem(KEY)) return undefined;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    const timer = window.setTimeout(() => { if (isIOS()) setShow(true); }, 12000);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); window.clearTimeout(timer); };
  }, []);

  const dismiss = () => { window.localStorage.setItem(KEY, new Date().toISOString()); setShow(false); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => null);
    dismiss();
  };

  if (!show) return null;
  return (
    <div className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[60] bg-[#0a1f44] text-white border-t-4 border-[#c8102e] shadow-2xl p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] animate-in slide-in-from-bottom-4" role="dialog" aria-label="Add BJP Patriots to your home screen" data-testid="install-prompt">
      <button type="button" onClick={dismiss} aria-label="Dismiss" data-testid="install-prompt-dismiss" className="absolute top-2 right-2 p-2 text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
      <div className="flex items-start gap-3">
        <img src={iceLogo} alt="" className="w-12 h-12 object-contain bg-white/10 p-1 shrink-0" />
        <div className="min-w-0 pr-6">
          <div className="font-heading uppercase tracking-[0.3em] text-[#f4b400] text-[9px]">Game day in your pocket</div>
          <div className="font-display text-xl leading-tight mt-0.5">ADD BJP PATRIOTS TO YOUR HOME SCREEN</div>
          <p className="text-xs text-white/75 mt-1.5">Open the schedule, announcements and game-day info like an app — even with a weak signal at the field.</p>
          {deferred ? (
            <button type="button" onClick={install} data-testid="install-prompt-install" className="mt-3 inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-3 min-h-[44px] transition-colors"><Download className="w-4 h-4" /> Install App</button>
          ) : (
            <div className="mt-3 text-xs text-white/85 flex items-center gap-2 bg-white/10 px-3 py-2.5" data-testid="install-prompt-ios-hint"><Share className="w-4 h-4 text-[#f4b400] shrink-0" /> Tap <strong>Share</strong>, then <strong>Add to Home Screen</strong></div>
          )}
        </div>
      </div>
    </div>
  );
};

export const OfflineBanner = () => {
  const [offline, setOffline] = useState(!window.navigator.onLine);
  useEffect(() => {
    const on = () => setOffline(false); const off = () => setOffline(true);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  if (!offline) return null;
  return <div className="bg-[#f4b400] text-[#0a1f44] text-center font-heading uppercase tracking-widest text-[10px] py-2 px-4" role="status" data-testid="offline-banner">You're offline — showing the last saved schedule and announcements</div>;
};
